import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { Paginated, ScraperResult, StagedEvent } from "@/types";

export function useStagedEvents(
  filters: {
    status?: string;
    search?: string;
    scraper?: string;
    hidePast?: boolean;
    cursor?: string;
    limit?: number;
  } = {},
) {
  const params = new URLSearchParams();
  if (filters.status && filters.status !== "all") params.set("status", filters.status);
  if (filters.search) params.set("search", filters.search);
  if (filters.scraper) params.set("scraper", filters.scraper);
  if (filters.hidePast) params.set("hidePast", "true");
  if (filters.cursor) params.set("cursor", filters.cursor);
  if (filters.limit) params.set("limit", String(filters.limit));

  const qs = params.toString();
  return useQuery({
    queryKey: ["scrapers", "staged", filters],
    queryFn: () => api.get<Paginated<StagedEvent>>(`/admin/scrapers/staged${qs ? `?${qs}` : ""}`),
  });
}

export function useOckStagedEvents(
  filters: {
    status?: string;
    search?: string;
    hidePast?: boolean;
    cursor?: string;
    limit?: number;
  } = {},
) {
  return useStagedEvents({ ...filters, scraper: "ock" });
}

export function useRunScraper() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<ScraperResult>("/admin/scrapers/ock", {}),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["scrapers", "staged"] }),
  });
}

export function useApproveStaged() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      venueId,
      categoryId,
      destination,
      title,
      eventDescription,
      date,
      registrationType,
      registrationValue,
    }: {
      id: string;
      venueId?: string;
      categoryId?: string;
      destination?: "events" | "movies";
      title?: string;
      eventDescription?: string;
      date?: string;
      registrationType?: string;
      registrationValue?: string;
    }) =>
      api.put<{ id: string }>(`/admin/scrapers/staged/${id}/approve`, {
        venueId,
        categoryId,
        destination,
        title,
        eventDescription,
        date,
        registrationType,
        registrationValue,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["scrapers", "staged"] }),
  });
}

export function useRejectStaged() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.put<{ ok: boolean }>(`/admin/scrapers/staged/${id}/reject`, {}),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["scrapers", "staged"] }),
  });
}

export function useBulkApproveStaged() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      ids,
      categoryId,
      destination,
    }: {
      ids: string[];
      categoryId?: string;
      destination?: "events" | "movies";
    }) =>
      api.post<{ results: Array<{ id: string; ok: boolean }> }>(
        "/admin/scrapers/staged/approve-bulk",
        { ids, categoryId, destination },
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["scrapers", "staged"] }),
  });
}

export function useBulkRejectStaged() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) =>
      api.post<{ results: Array<{ id: string; ok: boolean }> }>(
        "/admin/scrapers/staged/reject-bulk",
        { ids },
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["scrapers", "staged"] }),
  });
}

export function useEnrichStaged() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) =>
      api.post<{ results: Array<{ id: string; ok: boolean }> }>("/admin/scrapers/staged/enrich", {
        ids,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["scrapers", "staged"] }),
  });
}
